import { Square1 } from "./../puzzles/square1/square1";
import { Geometry } from "./../geometry/geometry";
import { Arrow } from "./../geometry/arrow";
import { getDefaultOptions } from "./options";
import { MASK_COLOR } from "./../puzzles/colors";
import { VisualizerType } from "./enum";
import { validatePuzzleOptions, } from "./interface";
import { Scene } from "../rendering/scene";
import { Camera } from "./../rendering/camera";
import { applyTransformations } from "../rendering/utils";
import { Group } from "../geometry/group";
import { getPuzzleGeometry, getPuzzleSimulator } from "./puzzleCreator";
import { Matrix4 } from "../math/matrix";
import { Quaternion } from "../math/quaternion";
/**
 * Applies a color scheme to simulator values
 *
 * @param faceValues face values from the simulator
 * @param scheme color scheme to
 */
function applyColorScheme(faceValues, scheme) {
    return Object.keys(faceValues).reduce((colors, face) => {
        colors[face] = faceValues[face].map((value) => scheme[value] || MASK_COLOR);
        return colors;
    }, {});
}
function isSquare1(type) {
    return type === VisualizerType.SQUARE1 || type === VisualizerType.SQUARE1_NET;
}
function isPyraminx(type) {
    return (type === VisualizerType.PYRAMINX || type === VisualizerType.PYRAMINX_NET);
}
function isMegaminx(type) {
    return (type === VisualizerType.MEGAMINX ||
        type === VisualizerType.MEGAMINX_NET ||
        type === VisualizerType.MEGAMINX_TOP);
}
/**
 * Return true if we can apply simulator colors. Currently
 * we don't simulate n-layered megaminx/pyraminx.
 */
function canApplySimulatorColors(type, size) {
    if (isPyraminx(type)) {
        return size === 3;
    }
    if (isMegaminx(type)) {
        return size === 2;
    }
    return true;
}
function createArrow(a, puzzle, group) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    // Get the face the arrow is pointing to
    let startFace = puzzle.faces[a.start.face];
    let endFace = puzzle.faces[a.end.face];
    if (!startFace || !endFace) {
        throw new Error(`Invalid arrow definition ${JSON.stringify(a)}`);
    }
    // Transform from sticker coordinates to group coordinates
    let startTransformations = [
        startFace.matrix,
        puzzle.group.matrix,
        group.matrix,
    ];
    let endTransformations = [endFace.matrix, puzzle.group.matrix, group.matrix];
    let start;
    let end;
    // Get the stickers on the face
    if (startFace instanceof Geometry && endFace instanceof Geometry) {
        start = (_a = startFace.faces[a.start.sticker]) === null || _a === void 0 ? void 0 : _a.centroid;
        end = (_b = endFace.faces[a.end.sticker]) === null || _b === void 0 ? void 0 : _b.centroid;
    }
    else {
        if (puzzle instanceof Square1) {
            start = (_c = startFace.objects[a.start.sticker]) === null || _c === void 0 ? void 0 : _c.faces[0].centroid;
            end = (_d = endFace.objects[a.end.sticker]) === null || _d === void 0 ? void 0 : _d.faces[0].centroid;
        }
        else {
            start = (_e = startFace.objects[a.start.sticker]) === null || _e === void 0 ? void 0 : _e.centroid;
            end = (_f = endFace.objects[a.end.sticker]) === null || _f === void 0 ? void 0 : _f.centroid;
        }
        startTransformations.unshift((_g = startFace.objects[a.start.sticker]) === null || _g === void 0 ? void 0 : _g.matrix);
        endTransformations.unshift((_h = endFace.objects[a.end.sticker]) === null || _h === void 0 ? void 0 : _h.matrix);
    }
    if (!start || !end) {
        throw new Error(`Invalid arrow definition ${JSON.stringify(a)}`);
    }
    let p1 = applyTransformations(start, startTransformations);
    let p2 = applyTransformations(end, endTransformations);
    return new Arrow(p1, p2);
}
/**
 * Encapsulates logic for setting up a puzzle environment for rendering
 * images. Sets up puzzle geometry, applies any algorithm or masking
 * if necessary, and renders the puzzle
 */
export class Visualizer {
    constructor(renderer, type, options = {}) {
        this.type = type;
        this.camera = new Camera();
        this.scene = new Scene();
        this.group = new Group();
        this.scene.add(this.group);
        this.renderer = renderer;
        this.initPuzzleOptions(options);
        this.puzzleGeometry = getPuzzleGeometry(this.type, this.options);
        this.simulator = getPuzzleSimulator(this.type, this.options);
        this.buildGroupMatrix();
        this.applyColors();
        this.addArrows();
        this.group.addObject(this.puzzleGeometry.group);
        this.render();
    }
    applyColors() {
        const hasCustomColors = this.options.stickerColors && !isSquare1(this.type);
        const canUseSimulator = canApplySimulatorColors(this.type, this.options.size);
        if (hasCustomColors) {
            this.puzzleGeometry.setColors(this.options.stickerColors);
        }
        else if (canUseSimulator) {
            this.applySimulatorColors();
        }
        else {
            // Apply scheme to puzzle geomety manually, for puzzles
            // not supported by simulators (megaminx != 2 pyraminx != 3)
            const faces = this.puzzleGeometry.faces;
            Object.keys(faces).forEach((face) => {
                const stickers = faces[face];
                const faceColor = this.options.scheme[face];
                if (stickers instanceof Geometry) {
                    stickers.faces.forEach((f) => (f.color = faceColor));
                }
                else if (stickers instanceof Group) {
                    stickers.objects.forEach((o) => (o.color = faceColor));
                }
            });
        }
    }
    applySimulatorColors() {
        if (this.options.mask)
            this.applyMask(this.options);
        if (this.options.alg || this.options.case)
            this.applyAlgorithm();
        const faceValues = this.simulator.getValues();
        const faceColors = applyColorScheme(faceValues, this.options.scheme);
        this.puzzleGeometry.setColors(faceColors);
    }
    applyAlgorithm() {
        if (isSquare1(this.type)) {
            // puzzle factory applies algorithm to square 1 when greating the puzzle geometry
            return;
        }
        if (this.options.case) {
            this.simulator.case(this.options.case);
        }
        else if (this.options.alg) {
            this.simulator.alg(this.options.alg);
        }
    }
    applyMask(options) {
        Object.keys(options.mask).forEach((maskedFace) => {
            options.mask[maskedFace].forEach((index) => this.simulator.setValue(maskedFace, index, "mask"));
        });
    }
    /**
     * build the group matrix for the puzzle. This sets up the
     * rotation, scale, and translation for the resulting rendered
     * image.
     */
    buildGroupMatrix() {
        this.group.matrix = new Matrix4();
        // Rotate the group matrix
        if (this.options.rotations) {
            this.options.rotations.forEach((rotation) => {
                const { x = 0, y = 0, z = 0 } = rotation;
                let rotationMatrix = Matrix4.fromQuaternion(Quaternion.fromEuler(x, y, z));
                Matrix4.multiply(this.group.matrix, rotationMatrix, this.group.matrix);
            });
        }
        // Scale the group matrix
        if (this.options.scale) {
            let scale = this.options.scale;
            this.group.matrix.scale(scale, scale, scale);
        }
        // Translate the group matrix
        if (this.options.translation) {
            const { x = 0, y = 0, z = 0 } = this.options.translation;
            let translationMatrix = Matrix4.fromTranslation(x, y, z);
            Matrix4.multiply(this.group.matrix, translationMatrix, this.group.matrix);
        }
    }
    addArrows() {
        if (!this.options.arrows) {
            return;
        }
        this.options.arrows.forEach((arrow) => {
            try {
                this.scene.add(createArrow(arrow, this.puzzleGeometry, this.group));
            }
            catch (e) {
                console.error(e);
                console.warn(`Invalid arrow ${JSON.stringify(arrow)}`);
            }
        });
    }
    initPuzzleOptions(options) {
        this.options = Object.assign(Object.assign({}, getDefaultOptions(this.type)), options);
        validatePuzzleOptions(this.options);
    }
    applyOptionsToPuzzle() {
        this.simulator.reset();
        this.buildGroupMatrix();
        this.applyColors();
        this.addArrows();
    }
    setPuzzleOptions(options) {
        this.initPuzzleOptions(options);
        // Handle square1 geometry separately, since it
        // changes, unlike the other puzzles
        if (isSquare1(this.type)) {
            this.puzzleGeometry = getPuzzleGeometry(this.type, this.options);
            this.group.setObjects([this.puzzleGeometry.group]);
        }
        this.applyOptionsToPuzzle();
    }
    render() {
        this.renderer.render(this.scene, this.camera);
    }
}
